/**
 * Domain types for the `validation` feature — a faithful mirror of the server's wire shapes
 * (server/src/modules/validation/* + prisma schema). These are TRANSPORT types: numbers that the
 * server emits as BigInt arrive as JSON strings, so every id is typed `string | number` and always
 * rendered/compared as a string on the client.
 *
 * Sources of truth:
 *   - GET  /validation/worklist            -> Resultat[] (repo.brutWorklist: statut ∈ {brut,repris})
 *   - POST /validation/signer-lot          -> SignOutcome[]
 *   - POST /validation/resultats/:id/signer-> SignOutcome
 *   - POST /validation/escalades/:id/acquitter  -> EscaladeCritique
 *   - POST /validation/escalades/:id/tentative  -> EscaladeTentative
 *   - POST /validation/escalades/:id/breakglass -> ValidationBreakGlass
 */

export type Id = string | number;

/** prisma enum DrapeauResultat. */
export type Drapeau = "normal" | "bas" | "haut" | "critique";

/** prisma enum StatutResultat (the worklist only ever returns brut | repris). */
export type StatutResultat = "brut" | "valide" | "corrige" | "annule" | "repris";

/** prisma enum StatutEscalade. */
export type StatutEscalade = "a_acquitter" | "acquitte" | "echec";

/** prisma enum CanalEscalade. */
export type CanalEscalade = "push_seam2" | "telephone" | "worklist_biologiste";

/** prisma enum ResultatTentative. */
export type ResultatTentative = "envoye" | "lu" | "sans_reponse" | "echec_technique";

/** The sign-off decision (validators.js: z.enum(["valide","rejete","repris"])). */
export type Decision = "valide" | "rejete" | "repris";

/**
 * A worklist line — a Resultat the bench handed to the biologiste (statut brut | repris).
 * A `corrige` re-open lands here as a fresh `brut` carrying remplace_resultat_id + version > 0.
 */
export interface WorklistResultat {
  id: Id;
  laboratoire_id?: Id;
  tube_id?: Id;
  parametre_labo_id?: Id;
  automate_id?: Id | null;

  valeur_numerique?: number | string | null;
  valeur_texte?: string | null;
  valeur_qualitative?: string | null;
  unite?: string | null;

  drapeau?: Drapeau | null;
  /** EG-46 (R3-H11) — numeric result ingested with NO resolvable reference range (source:'none'). */
  sans_reference?: boolean;
  statut: StatutResultat;

  /** D-24 supersede — set when this brut re-opened a prior valide for correction. */
  remplace_resultat_id?: Id | null;
  version?: number;

  created_at?: string;
  updated_at?: string;
}

/** One item of a batch sign-off request body (signBatchSchema.items[]). */
export interface SignItem {
  resultat_id: Id;
  decision?: Decision;
  acquittement_critique?: boolean;
  /** EG-46 (R3-H11) — explicit «signé sans intervalle de référence» acknowledgment. */
  acquittement_sans_reference?: boolean;
  commentaire?: string;
}

/** The per-result outcome of a sign-off (signoff.service.signOne/signBatch return shape). */
export interface SignOutcome {
  validation_id: Id;
  resultat_id: Id;
  decision: Decision;
  /** Non-null when a signed critique opened an EscaladeCritique loop (D-29). */
  escalade_id: Id | null;
}

/** A ladder attempt (escalade.service.recordTentative return = the created EscaladeTentative). */
export interface EscaladeTentative {
  id: Id;
  escalade_critique_id: Id;
  canal: CanalEscalade;
  resultat: ResultatTentative;
  notification_ref?: string | null;
  tente_par?: string | null;
  tente_at?: string;
}

/** The D-29 closed-loop entity (escalade.service returns the EscaladeCritique, essais included). */
export interface EscaladeCritique {
  id: Id;
  laboratoire_id?: Id;
  resultat_id: Id;
  validation_biologique_id: Id;
  medecin_ref?: Id | null;
  canal: CanalEscalade;
  statut: StatutEscalade;
  read_back_text?: string | null;
  acquitte_par?: string | null;
  acquitte_at?: string | null;
  tentatives: number;
  prochaine_tentative_at?: string | null;
  essais?: EscaladeTentative[];
}

/**
 * NOVA-051 — one durable escalation-worklist line (GET /validation/escalades?statut=…). A PHI-safe
 * projection of EscaladeCritique: opaque ids + the loop state only (never read_back_text / clinical value).
 */
export interface EscaladeWorklistItem {
  id: Id;
  resultat_id: Id | null;
  canal: CanalEscalade;
  statut: StatutEscalade;
  tentatives: number;
  prochaine_tentative_at?: string | null;
  acquitte_at?: string | null;
  created_at?: string | null;
}

/** NOVA-051 — the durable escalation worklist (GET /validation/escalades). */
export interface EscaladeWorklist {
  statut: StatutEscalade;
  escalades: EscaladeWorklistItem[];
}

/** The duty-to-warn override row (escalade.service.invokeBreakGlass / review return). */
export interface ValidationBreakGlass {
  id: Id;
  escalade_critique_id: Id;
  acteur_id: Id;
  medecin_ref?: Id | null;
  justification: string;
  portee: "fait_critique";
  reviewed: boolean;
  /** NOVA-023 — set once the manager post-hoc review closes the override (VAL-3). */
  reviewed_by?: Id | null;
  review_outcome?: string | null;
  created_at?: string;
}

/**
 * NOVA-028 — one affected released result inside a lot-recall worklist group (validation/recallWorklist).
 * The D-24 correction entry point: the biologiste re-decides each (statut valide | corrige).
 */
export interface RecallWorklistResultat {
  resultat_id: Id;
  tube_id?: Id | null;
  parametre_labo_id?: Id | null;
  valeur?: string | null;
  unite?: string | null;
  drapeau?: Drapeau | null;
  statut: "valide" | "corrige";
}

/** NOVA-028 — a recalled/expired lot + its affected released results (one worklist group). */
export interface RecallWorklistGroup {
  lot_id: Id;
  numero_lot: string;
  lot_statut: "quarantaine" | "perime";
  reactif_code?: string | null;
  reactif_libelle?: string | null;
  discipline_code?: string | null;
  affected_count: number;
  resultats: RecallWorklistResultat[];
}

/** NOVA-028 — the derived lot-recall worklist (GET /validation/recall-worklist). */
export interface RecallWorklist {
  groups: RecallWorklistGroup[];
  total_affected: number;
}

/** The server capability keys this feature gates on (validation/routes.js). */
export const CAP = {
  read: "validation.resultat.read",
  signoff: "validation.resultat.signoff",
  acknowledge: "validation.valeurcritique.acknowledge",
  /** NOVA-023 — the manager break-glass post-hoc review (VAL-3). */
  review: "validation.valeurcritique.review",
  /** NOVA-028 — the biologiste lot-recall worklist (INV-4 → D-24). */
  recallRead: "validation.recall.read",
} as const;

/** Map a result drapeau to the clinical descriptor status used by Badge / clinicalState. */
export function drapeauToClinical(d: Drapeau | null | undefined): "normal" | "low" | "high" | "critical" {
  switch (d) {
    case "critique":
      return "critical";
    case "haut":
      return "high";
    case "bas":
      return "low";
    default:
      return "normal";
  }
}

/** The single displayable value of a Resultat (numeric > texte > qualitative). */
export function resultValue(r: WorklistResultat): string {
  if (r.valeur_numerique != null && r.valeur_numerique !== "") return String(r.valeur_numerique);
  return r.valeur_texte ?? r.valeur_qualitative ?? "—";
}

/** A re-opened correction is a brut/repris result that supersedes a prior valide one. */
export function isCorrection(r: WorklistResultat): boolean {
  return r.remplace_resultat_id != null || (r.version ?? 0) > 0;
}

export function isCritical(r: WorklistResultat): boolean {
  return r.drapeau === "critique";
}

/** EG-46 (R3-H11) — an unrangeable numeric result: sign-off demands its own explicit ack. */
export function isSansReference(r: WorklistResultat): boolean {
  return r.sans_reference === true;
}
